import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Globe2 } from "lucide-react";
import { productPortfolio } from "../data/products";

const Page = styled.main`
  background: #f8fafc;
  color: #0f172a;
  min-height: 100vh;
  padding: 118px 0 78px;

  @media (max-width: 760px) {
    padding: 94px 0 58px;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.8fr);
  gap: 42px;
  align-items: end;
  margin-bottom: 28px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 11px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #2563eb;
  background: #ffffff;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  color: #0f172a;
  font-size: 3.6rem;
  line-height: 1.08;
  font-weight: 850;
  letter-spacing: 0;

  @media (max-width: 760px) {
    font-size: 2.3rem;
  }
`;

const Intro = styled.p`
  margin: 0;
  color: #475569;
  font-size: 1.02rem;
  line-height: 1.72;
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;

  .value {
    color: #0f172a;
    font-size: 1.8rem;
    font-weight: 850;
    line-height: 1;
  }

  .label {
    margin-top: 8px;
    color: #64748b;
    font-size: 0.88rem;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.article)`
  display: grid;
  grid-template-columns: 76px 1fr;
  gap: 20px;
  padding: 22px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const IconBox = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #dbeafe;
`;

const ProductBody = styled.div`
  min-width: 0;

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .status,
  .category {
    min-height: 28px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 9px;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 850;
  }

  .status {
    color: #166534;
    background: #dcfce7;
  }

  .category {
    color: #475569;
    background: #f1f5f9;
  }

  h2 {
    color: #0f172a;
    font-size: 1.45rem;
    line-height: 1.2;
    letter-spacing: 0;
    margin: 0 0 10px;
  }

  p {
    color: #475569;
    line-height: 1.65;
    font-size: 0.96rem;
    margin: 0;
  }

  .features {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin: 16px 0 18px;
  }

  .features span {
    min-height: 27px;
    display: inline-flex;
    align-items: center;
    padding: 0 9px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #475569;
    background: #f8fafc;
    font-size: 0.76rem;
    font-weight: 750;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 42px;
    padding: 0 14px;
    border-radius: 8px;
    color: #ffffff;
    background: #0f172a;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 850;
  }
`;

const Products = () => {
  return (
    <Page>
      <Inner>
        <Header>
          <div>
            <Eyebrow>Product portfolio</Eyebrow>
            <Title>AI-enabled SaaS products for daily business operations.</Title>
          </div>
          <Intro>
            Willovate products are built for practical workflows: counters, projects, memberships, campaigns, restaurant operations, reporting and AI-assisted decisions.
          </Intro>
        </Header>

        <Summary>
          <SummaryCard>
            <div className="value">{productPortfolio.length}</div>
            <div className="label">Live SaaS products</div>
          </SummaryCard>
          <SummaryCard>
            <div className="value">AI</div>
            <div className="label">Insights, reports and automation where they help</div>
          </SummaryCard>
          <SummaryCard>
            <div className="value">Ops</div>
            <div className="label">Designed around real operational workflows</div>
          </SummaryCard>
        </Summary>

        <ProductGrid>
          {productPortfolio.map((product, index) => {
            const Icon = product.icon;
            return (
              <ProductCard
                key={product.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
              >
                <IconBox>
                  <Icon size={30} />
                </IconBox>
                <ProductBody>
                  <div className="meta">
                    <span className="status"><CheckCircle2 size={14} /> Live</span>
                    <span className="category"><Globe2 size={14} /> {product.category}</span>
                  </div>
                  <h2>{product.name}</h2>
                  <p>{product.description}</p>
                  <div className="features">
                    {product.features.map((feature) => (
                      <span key={feature}>{feature}</span>
                    ))}
                  </div>
                  <a href={product.url} target="_blank" rel="noopener noreferrer">
                    Visit product
                    <ArrowUpRight size={16} />
                  </a>
                </ProductBody>
              </ProductCard>
            );
          })}
        </ProductGrid>
      </Inner>
    </Page>
  );
};

export default Products;
